import React, { useState, useEffect } from 'react';
import { CommentReaction } from 'types/task';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

interface CommentReactionsProps {
  reactions: CommentReaction[];
  onToggle: (emoji: string) => void;
}

const MAX_DISPLAYED_USERS = 5;

const CommentReactions: React.FC<CommentReactionsProps> = ({ reactions, onToggle }) => {
  const [hoveredReaction, setHoveredReaction] = useState<{ reaction: CommentReaction; x: number; y: number } | null>(null);
  const lastClickRef = React.useRef<number>(0);

  const handleClick = (emoji: string) => {
    const now = Date.now();
    if (now - lastClickRef.current < 500) return; // 500ms cooldown to prevent spam
    lastClickRef.current = now;
    onToggle(emoji);
  };

  // Hide tooltip on scroll to cleanly release coordinates
  useEffect(() => {
    if (!hoveredReaction) return;
    const hideTooltip = () => setHoveredReaction(null);
    window.addEventListener('scroll', hideTooltip, true);
    return () => window.removeEventListener('scroll', hideTooltip, true);
  }, [hoveredReaction]);

  if (!reactions || reactions.length === 0) return null;

  const handleMouseEnter = (e: React.MouseEvent, reaction: CommentReaction) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredReaction({
      reaction,
      x: rect.left + rect.width / 2,
      y: rect.top - 8, // 8px spacing upward
    });
  };

  const handleMouseLeave = () => {
    setHoveredReaction(null);
  };

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      <AnimatePresence initial={false}>
        {reactions.map((reaction) => (
          <motion.button
            key={reaction.emoji}
            layout
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            whileHover={{ y: -1, scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleClick(reaction.emoji)}
            onMouseEnter={(e) => handleMouseEnter(e, reaction)}
            onMouseLeave={handleMouseLeave}
            className={`
              inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-300 relative
              ${reaction.reactedByCurrentUser 
                ? 'bg-primary-subtle/40 border-[#7C6CF7]/50 text-[#7C6CF7] shadow-[0_0_12px_rgba(124,108,247,0.08)]' 
                : 'bg-primary-variant/30 border-primary-subtle text-neutral-medium hover:border-neutral-dark hover:bg-primary-variant/60'}
            `}
          >
            <span className="text-[15px] leading-none">{reaction.emoji}</span>
            <span className={`text-[11px] ${reaction.reactedByCurrentUser ? 'text-[#7C6CF7] font-bold' : 'text-neutral-light'}`}>
              {reaction.count}
            </span>
          </motion.button>
        ))}
      </AnimatePresence>

      {hoveredReaction && createPortal(
        <div 
          className="fixed z-[9999] pointer-events-none -translate-x-1/2 -translate-y-full animate-in fade-in zoom-in-95 duration-200 origin-bottom"
          style={{ top: hoveredReaction.y, left: hoveredReaction.x }}
        >
          <div className="bg-[#1A1A1E]/95 backdrop-blur-xl text-white py-2 px-3.5 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/10 min-w-max">
            <div className="flex flex-col gap-1.5">
              <span className="text-[9px] font-black uppercase tracking-[0.05em] text-white/40 leading-none mb-0.5">Reacted by</span>
              <div className="flex flex-col items-start gap-1">
                {hoveredReaction.reaction.reactedUsers.slice(0, MAX_DISPLAYED_USERS).map((u) => (
                  <span key={u.appUserUuid} className="text-[11px] font-medium text-white/90 truncate max-w-[200px]">
                    {u.appUserFullName}
                  </span>
                ))}
                {hoveredReaction.reaction.count > MAX_DISPLAYED_USERS && (
                  <span className="text-[11px] font-bold text-[#7C6CF7] mt-0.5">
                    + {hoveredReaction.reaction.count - MAX_DISPLAYED_USERS} others
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Modern Arrow */}
          <div className="w-2 h-2 bg-[#1A1A1E]/95 backdrop-blur-xl rotate-45 border-r border-b border-white/10 mx-auto -mt-1 relative z-10 hidden sm:block"></div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CommentReactions;
